import React from "react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Separator } from "../components/ui/separator";
import { Textarea } from "../components/ui/textarea";

import { useMemo, useState } from "react";
import { useBaseOwnerId, useModelCreate } from "@rhino-project/core/hooks";

export default function SignalCaptureTriage() {
    const capabilityOptions = ["Autonomy", "Cyber", "Sensing", "Comms", "Logistics", "Materials"];
    const [summary, setSummary] = useState("");
    const [market, setMarket] = useState("");
    const [trl, setTrl] = useState("");
    const [readiness, setReadiness] = useState("");
    const [selectedCapabilities, setSelectedCapabilities] = useState<string[]>([]);
    const organizationId = useBaseOwnerId();
    const { mutate: createSubmission, isPending, error } = useModelCreate("submission" as any);
    const [signal, setSignal] = useState({
      read: "Awaiting intake details.",
      capability: "Not generated",
      endUser: "Not assigned",
      weakSignal: "Unknown",
      confidence: "Draft",
    });
    const strongestCapability = useMemo(() => selectedCapabilities[0] ?? "General-purpose capability", [selectedCapabilities]);
    const toggleCapability = (capability: string) => {
      setSelectedCapabilities((prev) => prev.includes(capability) ? prev.filter((item) => item !== capability) : [...prev, capability]);
    };
    const generateSignal = () => {
      createSubmission({ organization_id: Number.isFinite(organizationId) ? organizationId : undefined, company_name: market || "Dual-use applicant", product_description: summary, capabilities: selectedCapabilities, trl, market_readiness: readiness, status: "triaged" } as any);
      setSignal({
        read: `Commercial narrative mapped for ${market || "multi-domain"} application with ${trl || "unspecified TRL"} maturity and ${readiness || "emerging"} readiness.`,
        capability: strongestCapability,
        endUser: strongestCapability === "Cyber" ? "CAF cyber operations" : "Joint capability sponsors",
        weakSignal: selectedCapabilities.length === 0 ? "Potential weak signal: add evidence and capability tags." : "No immediate weak-signal flag",
        confidence: selectedCapabilities.length >= 2 ? "High confidence" : "Moderate confidence",
      });
    };
  return (
   <div className="w-full min-w-0 bg-background text-foreground overflow-x-hidden">
      <div className="mx-auto flex w-full max-w-7xl min-w-0 flex-col gap-6 px-4 py-6 sm:px-6 lg:px-8">
          <section className="w-full">
      <div className="grid gap-6 lg:grid-cols-2">
      <Card className="border-border shadow-sm">
      <CardHeader>
      <CardTitle>Signal Capture Intake</CardTitle>
      <CardDescription>Capture solution summary, maturity, and market evidence to generate a first triage signal.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
      <div className="space-y-2">
      <Label htmlFor="summary">Solution summary</Label>
      <Textarea id="summary" value={summary} onChange={(event) => setSummary(event.target.value)} className="min-h-28" />
      </div>
      <div className="space-y-2">
      <Label htmlFor="market">Target market</Label>
      <Input id="market" value={market} onChange={(event) => setMarket(event.target.value)} placeholder="Commercial sector or deployment context" />
      </div>
      <div className="grid gap-4 md:grid-cols-2">
      <div className="space-y-2">
      <Label>TRL band</Label>
      <Select value={trl} onValueChange={setTrl}>
      <SelectTrigger><SelectValue placeholder="Select TRL" /></SelectTrigger>
      <SelectContent className="bg-popover text-popover-foreground">
      <SelectItem value="trl-1-3">TRL 1-3</SelectItem>
      <SelectItem value="trl-4-6">TRL 4-6</SelectItem>
      <SelectItem value="trl-7-9">TRL 7-9</SelectItem>
      </SelectContent>
      </Select>
      </div>
      <div className="space-y-2">
      <Label>Market readiness</Label>
      <Select value={readiness} onValueChange={setReadiness}>
      <SelectTrigger><SelectValue placeholder="Select readiness" /></SelectTrigger>
      <SelectContent className="bg-popover text-popover-foreground">
      <SelectItem value="pilot">Pilot</SelectItem>
      <SelectItem value="commercialized">Commercialized</SelectItem>
      <SelectItem value="scaled">Scaled</SelectItem>
      </SelectContent>
      </Select>
      </div>
      </div>
      <div className="space-y-2">
      <Label>Capabilities</Label>
      <div className="grid grid-cols-2 gap-2 text-sm">
      {capabilityOptions.map((option) => (
      <label key={option} className="flex items-center gap-2 rounded-md border border-border px-3 py-2">
      <Checkbox checked={selectedCapabilities.includes(option)} onCheckedChange={() => toggleCapability(option)} />
      <span>{option}</span>
      </label>
      ))}
      </div>
      </div>
      <Button onClick={generateSignal} disabled={!summary.trim() || isPending}>{isPending ? "Generating..." : "Generate Signal Read"}</Button>
      {error && <p className="text-sm text-destructive">Unable to save signal read. Please confirm your intake details.</p>}
      </CardContent>
      </Card>

      <Card className="border-primary/20 shadow-sm">
      <CardHeader>
      <CardTitle>Signal Output</CardTitle>
      <CardDescription>Auto-generated and editable framing for downstream translation and sponsor review.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
      <Badge variant="secondary">{signal.confidence}</Badge>
      <p className="text-sm text-foreground"><span className="font-medium">Signal read:</span> {signal.read}</p>
      <Separator />
      <p className="text-sm text-muted-foreground"><span className="font-medium text-foreground">Mapped capability:</span> {signal.capability}</p>
      <p className="text-sm text-muted-foreground"><span className="font-medium text-foreground">Likely end-user:</span> {signal.endUser}</p>
      <p className="text-sm text-muted-foreground"><span className="font-medium text-foreground">Weak signal:</span> {signal.weakSignal}</p>
      </CardContent>
      </Card>
      </div>
      </section>
      </div>
    </div>
  );
}